import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import { isAgencyChattable, isAgencyEditable } from '@saaga/shared/utils/agency-visibility.util'
import type { ReactNode } from 'react'

import { AgencyGrid } from '@/components/agencies/agency-grid.organism'

import { AgenciesEmptyStateView } from './agencies-empty-state.view'
import { AgenciesErrorAlert } from './agencies-error-alert.atom'
import { AgenciesFilterActions } from './agencies-filter-actions.atom'
import type { UseAgenciesFilterReturn } from './use-agencies-filter.hook'
import type { UseAgenciesNavigationReturn } from './use-agencies-navigation.hook'
import type { UseAgencyDeleteConfirmReturn } from './use-agency-delete-confirm.hook'

interface AgenciesGridContentProps {
  currentUserId: string | undefined
  filter: UseAgenciesFilterReturn
  navigation: UseAgenciesNavigationReturn
  deleteConfirm: UseAgencyDeleteConfirmReturn
}

const checkAgencyEditable = (agency: Agency): boolean => isAgencyEditable(agency.visibility)
const checkAgencyChattable = (agency: Agency): boolean => isAgencyChattable(agency.visibility)

const buildDeletableCheck =
  (currentUserId: string | undefined): ((agency: Agency) => boolean) =>
  (agency: Agency): boolean =>
    isAgencyEditable(agency.visibility) && agency.userId === currentUserId

export const AgenciesGridContent = ({
  currentUserId,
  filter,
  navigation,
  deleteConfirm,
}: AgenciesGridContentProps): ReactNode => {
  const isOwnerTab = filter.activeTab === 'studio' || filter.activeTab === 'organization'
  const checkAgencyDeletable = buildDeletableCheck(currentUserId)

  return (
    <>
      <AgenciesFilterActions
        activeTab={filter.activeTab}
        selectedCategoryList={filter.selectedCategoryList}
        onCategoryToggle={filter.handleCategoryToggle}
        onClearAll={filter.handleClearCategoryFilter}
        onCreateAgency={navigation.handleCreateAgency}
      />
      {filter.isError && <AgenciesErrorAlert />}
      {!filter.isError && (
        <AgencyGrid
          agencyList={filter.filteredAgencyList}
          pinnedAgencyIdList={filter.pinnedAgencyIdList}
          isLoading={filter.isLoading}
          isVisibilityBadgeVisible={isOwnerTab}
          isAgencyEditable={checkAgencyEditable}
          isAgencyDeletable={checkAgencyDeletable}
          isStartChatVisible={checkAgencyChattable}
          onStartChat={navigation.handleStartChat}
          onSeeDetails={navigation.handleSeeDetails}
          onEdit={isOwnerTab ? navigation.handleEditAgency : undefined}
          onDelete={deleteConfirm.handleDeleteRequest}
          onAgencyClick={navigation.handleAgencyClick}
          onPin={filter.handlePin}
          emptyState={
            <AgenciesEmptyStateView
              activeTab={filter.activeTab}
              onCreateAgency={navigation.handleCreateAgency}
            />
          }
        />
      )}
    </>
  )
}
